import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Sparkles, RefreshCw, Facebook, Instagram, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/messenger`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const PlatformIcon = ({ p, className = "h-3.5 w-3.5" }) =>
  p === "instagram" ? <Instagram className={`${className} text-pink-400`} /> : <Facebook className={`${className} text-sky-400`} />;

export default function Messenger() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadConvs = async () => {
    try { setConvs((await axios.get(`${API}/conversations`)).data.items || []); }
    catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };
  const openConv = async (c) => {
    setActive(c); setText("");
    const r = await axios.get(`${API}/conversations/${encodeURIComponent(c.conversation_id)}/messages`);
    setMsgs(r.data.items || []);
    window.dispatchEvent(new Event("messenger-read"));
    setConvs((prev) => prev.map((x) => (x.conversation_id === c.conversation_id ? { ...x, unread: false } : x)));
  };
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/conversations/${encodeURIComponent(active.conversation_id)}/reply`, { text });
      setText(""); await openConv(active); loadConvs();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const suggest = async () => {
    setBusy(true);
    try { setText((await axios.post(`${API}/conversations/${encodeURIComponent(active.conversation_id)}/ai-suggest`)).data.suggestion || ""); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex h-[calc(100vh-65px)]" data-testid="messenger-page">
      <aside className="w-80 flex-shrink-0 overflow-y-auto border-r border-zinc-800 bg-zinc-950/60">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h1 className="text-sm font-semibold text-zinc-100">Messenger & Instagram</h1>
          <button onClick={loadConvs} data-testid="messenger-refresh" className="text-zinc-400 hover:text-zinc-100"><RefreshCw className="h-4 w-4" /></button>
        </div>
        {convs.length === 0 && <p className="p-4 text-xs text-zinc-500" data-testid="messenger-empty">Nicio conversație încă.</p>}
        {convs.map((c) => (
          <button key={c.conversation_id} onClick={() => openConv(c)} data-testid={`conv-${c.conversation_id}`}
            className={`flex w-full items-center gap-3 border-b border-zinc-900 px-4 py-3 text-left transition-colors hover:bg-zinc-900 ${active?.conversation_id === c.conversation_id ? "bg-zinc-900" : ""}`}>
            {c.profile_pic ? <img src={c.profile_pic} alt="" className="h-9 w-9 rounded-full object-cover" />
              : <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-xs text-zinc-400">{(c.name || "?")[0]}</div>}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <PlatformIcon p={c.platform} />
                <span className={`truncate text-sm ${c.unread ? "font-bold text-zinc-50" : "text-zinc-200"}`}>{c.name || c.customer_id}</span>
                {c.unread && <span className="ml-auto h-2 w-2 rounded-full bg-orange-500" data-testid={`conv-unread-${c.conversation_id}`} />}
              </div>
              <p className="truncate text-[11px] text-zinc-500">{c.last_from_page ? "Tu: " : ""}{c.last_message_text}</p>
              <p className="text-[10px] text-zinc-600">{fmt(c.last_message_at)}</p>
            </div>
          </button>
        ))}
      </aside>
      <section className="flex flex-1 flex-col">
        {!active ? <p className="m-auto text-sm text-zinc-500">Alege o conversație</p> : (
          <>
            <header className="flex items-center gap-2 border-b border-zinc-800 px-5 py-3">
              <PlatformIcon p={active.platform} className="h-4 w-4" />
              <span className="text-sm font-semibold text-zinc-100" data-testid="messenger-active-name">{active.name || active.customer_id}</span>
              {!active.can_reply && <span className="ml-auto inline-flex items-center gap-1 rounded bg-rose-500/15 px-2 py-0.5 text-[10px] text-rose-200" data-testid="messenger-window-closed"><Clock className="h-3 w-3" /> Peste 24h — Meta nu permite răspuns</span>}
            </header>
            <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4" data-testid="messenger-messages">
              {msgs.map((m) => (
                <div key={m.mid} className={`flex ${m.from_page ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${m.from_page ? "bg-orange-500/20 text-orange-50" : "bg-zinc-800 text-zinc-100"}`}>
                    {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                    {(m.attachments || []).map((a, i) => (a.type === "image"
                      ? <img key={i} src={a.url} alt="" className="mt-1 max-h-60 rounded-lg" />
                      : <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block text-xs underline">[{a.type}]</a>))}
                    <p className="mt-0.5 text-right text-[9px] opacity-60">{fmt(m.created_at)}</p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <footer className="border-t border-zinc-800 p-3">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} disabled={!active.can_reply}
                placeholder={active.can_reply ? "Scrie răspunsul..." : "Fereastra de 24h a expirat"} data-testid="messenger-reply-input"
                className="w-full resize-none rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50 disabled:opacity-50" />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={suggest} disabled={busy || !active.can_reply} data-testid="messenger-ai-suggest"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs text-violet-200 disabled:opacity-50">
                  <Sparkles className="h-3.5 w-3.5" /> Sugestie AI
                </button>
                <button onClick={send} disabled={busy || !text.trim() || !active.can_reply} data-testid="messenger-send"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
                  <Send className="h-3.5 w-3.5" /> Trimite
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
